(() => {
  if (window.AlcimoCartSync) return;

  const SELECTOR = '[data-alcimo-cart-count]';
  const SHARED_COOKIE = 'alcimo_cart_count';
  const SHARED_COOKIE_MAX_AGE = 60 * 60 * 24 * 30;
  const SCOPE_COOKIE = 'alcimo_customer_scope';
  const GUEST_SCOPE = 'guest';
  const SNAPSHOT_PREFIX = 'alcimo:cart:';
  const CART_RESTORE_PARAMETER = 'cartRestore';
  const MAX_LINES = 50;
  const MAX_QUANTITY = 99;

  const sanitizeScope = (scope) => {
    const value = String(scope || '').trim();
    return /^[a-z0-9_-]{1,80}$/i.test(value) ? value : '';
  };

  const readScope = () => {
    try {
      const prefix = `${SCOPE_COOKIE}=`;
      const item = document.cookie.split('; ').find((cookie) => cookie.startsWith(prefix));
      return item ? sanitizeScope(decodeURIComponent(item.slice(prefix.length))) || GUEST_SCOPE : GUEST_SCOPE;
    } catch {
      return GUEST_SCOPE;
    }
  };

  const normalizeSnapshot = (items) => {
    if (!Array.isArray(items)) return [];
    const byVariant = new Map();

    items.forEach((item) => {
      const variantId = String(item?.variantId ?? item?.variant_id ?? item?.id ?? '').trim();
      const quantity = Math.max(0, Math.min(MAX_QUANTITY, Number.parseInt(item?.quantity, 10) || 0));
      if (!/^\d+$/.test(variantId) || quantity <= 0) return;
      byVariant.set(variantId, Math.min(MAX_QUANTITY, (byVariant.get(variantId) || 0) + quantity));
    });

    return [...byVariant.entries()]
      .slice(0, MAX_LINES)
      .map(([variantId, quantity]) => ({variantId, quantity}));
  };

  const snapshotFromCart = (cart) =>
    normalizeSnapshot(
      (cart?.items || []).map((item) => ({
        variantId: item.variant_id,
        quantity: item.quantity,
      })),
    );

  const storageKey = (scope = readScope()) => `${SNAPSHOT_PREFIX}${sanitizeScope(scope) || GUEST_SCOPE}`;

  const writeSnapshot = (items, scope = readScope()) => {
    const normalized = normalizeSnapshot(items);
    try {
      window.localStorage.setItem(storageKey(scope), JSON.stringify(normalized));
    } catch (error) {
      console.warn('ALCIMO: não foi possível salvar o carrinho da conta.', error);
    }
    return normalized;
  };

  const readSnapshot = (scope = readScope()) => {
    try {
      return normalizeSnapshot(JSON.parse(window.localStorage.getItem(storageKey(scope)) || '[]'));
    } catch {
      return [];
    }
  };

  const persistSharedCount = (count) => {
    try {
      const secure = window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie =
        `${SHARED_COOKIE}=${encodeURIComponent(String(count))}; ` +
        `Path=/; Domain=.alcimo.com; ` +
        `Max-Age=${SHARED_COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
    } catch (error) {
      console.warn('ALCIMO: não foi possível compartilhar o contador do carrinho.', error);
    }
  };

  const animate = (element) => {
    element.classList.remove('is-updating');
    void element.offsetWidth;
    element.classList.add('is-updating');
    window.setTimeout(() => element.classList.remove('is-updating'), 600);
  };

  const setCount = (value, options = {}) => {
    const count = Math.max(0, Number.parseInt(value, 10) || 0);
    const counters = document.querySelectorAll(SELECTOR);

    counters.forEach((counter) => {
      const previous = Number.parseInt(counter.textContent, 10) || 0;
      counter.textContent = String(count);
      counter.hidden = count === 0;
      counter.setAttribute('aria-label', count === 1 ? '1 item no carrinho' : `${count} itens no carrinho`);
      if (options.animate !== false && previous !== count && count > 0) animate(counter);
    });

    document.documentElement.dataset.cartItemCount = String(count);
    persistSharedCount(count);
    window.dispatchEvent(new CustomEvent('alcimo:cart-count-updated', {detail: {itemCount: count}}));
    return count;
  };

  const getCartUrl = () => (window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart');

  const fetchCart = async () => {
    const response = await fetch(`${getCartUrl()}.js?_=${Date.now()}`, {
      method: 'GET',
      headers: {Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest'},
      cache: 'no-store',
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Falha ao consultar o carrinho: ${response.status}`);
    return response.json();
  };

  const refresh = async (options = {}) => {
    try {
      const cart = await fetchCart();
      writeSnapshot(snapshotFromCart(cart));
      setCount(cart.item_count, options);
      return cart;
    } catch (error) {
      console.warn('ALCIMO: não foi possível sincronizar o contador.', error);
      return null;
    }
  };

  const showRestoredMessage = (count) => {
    if (!count || document.querySelector('[data-alcimo-cart-restored]')) return;
    const toast = document.createElement('div');
    toast.setAttribute('data-alcimo-cart-restored', '');
    toast.setAttribute('role', 'status');
    toast.textContent = count === 1
      ? 'Você tinha 1 item no carrinho. Restauramos para você.'
      : `Você tinha ${count} itens no carrinho. Restauramos para você.`;
    Object.assign(toast.style, {
      position: 'fixed', right: '20px', bottom: '20px', zIndex: '100001',
      maxWidth: '360px', padding: '14px 18px', background: '#111', color: '#fff',
      fontSize: '13px', lineHeight: '1.45', boxShadow: '0 10px 30px rgba(0,0,0,.22)'
    });
    document.body.appendChild(toast);
    window.setTimeout(() => toast.remove(), 5000);
  };

  const restoreSnapshot = async (items, options = {}) => {
    const snapshot = normalizeSnapshot(items);
    const cartUrl = getCartUrl();

    try {
      const clearResponse = await fetch(`${cartUrl}/clear.js`, {
        method: 'POST',
        headers: {Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest'},
        credentials: 'same-origin',
      });
      if (!clearResponse.ok) throw new Error(`Falha ao limpar o carrinho: ${clearResponse.status}`);

      if (snapshot.length) {
        const addResponse = await fetch(`${cartUrl}/add.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            items: snapshot.map((item) => ({id: Number(item.variantId), quantity: item.quantity})),
          }),
        });
        if (!addResponse.ok) throw new Error(`Falha ao restaurar o carrinho: ${addResponse.status}`);
      }

      const cart = await fetchCart();
      setCount(cart.item_count, {animate: false});
      writeSnapshot(snapshotFromCart(cart));
      if (options.notify !== false && cart.item_count > 0) showRestoredMessage(cart.item_count);
      return cart;
    } catch (error) {
      console.warn('ALCIMO: não foi possível restaurar o carrinho salvo.', error);
      return null;
    }
  };

  let restorePromise = null;

  const snapshotsEqual = (left, right) => {
    const a = normalizeSnapshot(left);
    const b = normalizeSnapshot(right);

    if (a.length !== b.length) return false;

    const quantities = new Map(
      a.map((item) => [String(item.variantId), Number(item.quantity)]),
    );

    return b.every(
      (item) =>
        quantities.get(String(item.variantId)) === Number(item.quantity),
    );
  };

  const restoreFromAccountReturn = async () => {
    const currentUrl = new URL(window.location.href);
    if (!currentUrl.searchParams.has(CART_RESTORE_PARAMETER)) return false;

    // DOMContentLoaded e pageshow podem acontecer quase juntos.
    // Uma única restauração pode rodar por vez.
    if (restorePromise) return restorePromise;

    let snapshot = [];
    try {
      snapshot = normalizeSnapshot(
        JSON.parse(currentUrl.searchParams.get(CART_RESTORE_PARAMETER) || '[]'),
      );
    } catch {
      snapshot = [];
    }

    // Remove o parâmetro ANTES de qualquer chamada assíncrona. Assim um segundo
    // evento não tenta restaurar o mesmo carrinho novamente.
    currentUrl.searchParams.delete(CART_RESTORE_PARAMETER);
    window.history.replaceState(
      window.history.state,
      document.title,
      `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`,
    );

    restorePromise = (async () => {
      try {
        const currentCart = await fetchCart();
        const currentSnapshot = snapshotFromCart(currentCart);

        // Se o carrinho que já está na loja é exatamente o mesmo da conta,
        // não restaura nem mostra a mensagem. Isso cobre a navegação normal
        // Loja -> Perfil -> Carrinho sem duplicar os produtos.
        if (snapshotsEqual(currentSnapshot, snapshot)) {
          setCount(currentCart.item_count, {animate: false});
          writeSnapshot(currentSnapshot);
          return true;
        }

        writeSnapshot(snapshot);
        const cart = await restoreSnapshot(snapshot, {notify: true});
        return Boolean(cart);
      } finally {
        restorePromise = null;
      }
    })();

    return restorePromise;
  };

  window.AlcimoCartSync = {
    setCount,
    refresh,
    getSnapshot: () => readSnapshot(),
    getSnapshotForScope: (scope) => readSnapshot(scope),
    writeSnapshot,
    restoreSnapshot,
  };

  document.addEventListener('DOMContentLoaded', async () => {
    const restored = await restoreFromAccountReturn();
    if (!restored) await refresh({animate: false});
  });

  window.addEventListener('pageshow', async () => {
    const restored = await restoreFromAccountReturn();
    if (!restored) await refresh({animate: false});
  });
})();
